import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { BlockIcon, ExternalIcon, NotForMeIcon, PlayIcon } from '../components/Icons';
import { useToast } from '../components/Toast';
import { DISCOVER_WEEKLY_URL } from '../config';
import { useHiddenRecs, useTaste } from '../lib/localPrefs';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { arrangeRecommendations, getRecommendations, peekRecommendations, type RecFilter, type Recommendation } from '../recommendations/service';
import './home.css';

const FILTERS: { id: RecFilter; label: string; short: string }[] = [
  { id: 'all', label: 'All', short: 'All' },
  { id: 'doc', label: 'From your doc', short: 'Your doc' },
  { id: 'searches', label: 'From your searches', short: 'Your searches' },
];

/** Mock "Updated" line (FR-H1): Discover Weekly refreshes on Mondays. */
function weeklyLine(now = new Date()) {
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  const daysLeft = 7 - ((now.getDay() + 6) % 7);
  const date = monday.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  return { updated: `Updated ${date}`, next: `New mix in ${daysLeft} day${daysLeft === 1 ? '' : 's'}` };
}

function DiscoverWeekly({ covers }: { covers: string[] }) {
  const line = weeklyLine();
  return (
    <section className="dw" aria-labelledby="dw-title">
      <div className="dw-mosaic" aria-hidden="true">
        {covers.slice(0, 4).map((c) => (
          <img key={c} src={c} alt="" width={84} height={84} />
        ))}
      </div>
      <div className="dw-body">
        <div className="eyebrow" style={{ color: 'var(--accent-soft)' }}>
          Your weekly mix
        </div>
        <h1 id="dw-title" className="disp dw-title">
          Discover Weekly
        </h1>
        <p className="dw-meta">
          30 tracks · <span className="desktop-only">{line.updated} · </span>
          {line.next}
        </p>
      </div>
      <a href={DISCOVER_WEEKLY_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary dw-play">
        <PlayIcon />
        Play in Spotify
        <span className="visually-hidden">(opens Spotify)</span>
      </a>
    </section>
  );
}

function RecCard({ r, onHide }: { r: Recommendation; onHide: (r: Recommendation) => void }) {
  return (
    <article className="card rec">
      <a
        href={r.url}
        target="_blank"
        rel="noopener noreferrer"
        className="cover rec-cover"
        aria-label={`Open ${r.title} by ${r.artist} in Spotify (opens in a new tab)`}
      >
        <img src={r.cover} alt="" loading="lazy" decoding="async" width={300} height={300} />
        <span className="cover-open" aria-hidden="true">
          <span className="btn btn-primary btn-sm">
            Open in Spotify <ExternalIcon size={14} />
          </span>
        </span>
      </a>
      <div className="rec-text">
        <a href={r.url} target="_blank" rel="noopener noreferrer" className="rec-title ellipsis" tabIndex={-1}>
          {r.title}
        </a>
        <span className="rec-artist ellipsis">
          {r.artistId ? <Link to={`/artist/${r.artistId}`}>{r.artist}</Link> : <Link to={`/search?q=${encodeURIComponent(r.artist)}`}>{r.artist}</Link>}{' '}
          <span className="mono muted" style={{ fontSize: 12 }}>
            · {r.year}
          </span>
        </span>
        <span className="rec-reason">
          <span className={`dot${r.source === 'searches' ? ' square' : ''}`} aria-hidden="true" />
          <span>
            <span className="visually-hidden">{r.source === 'doc' ? 'From your doc: ' : 'From your searches: '}</span>
            {r.reason}
          </span>
        </span>
      </div>
      <button type="button" className="icon-btn rec-hide" aria-label={`Not for me — hide ${r.title}`} title="Not for me" onClick={() => onHide(r)}>
        <NotForMeIcon />
      </button>
    </article>
  );
}

export function HomePage() {
  useDocumentTitle(null);
  const [taste] = useTaste();
  const [hidden, setHidden] = useHiddenRecs();
  const [filter, setFilter] = useState<RecFilter>('all');
  const [showSinglesEps, setShowSinglesEps] = useState(false);
  const toast = useToast();
  // Mock data never fails (resilience requirement); real logic slots in behind this query.
  const { data: recs = [], isPending } = useQuery({
    queryKey: ['recommendations', taste],
    queryFn: () => getRecommendations(taste),
    initialData: () => peekRecommendations(taste),
    staleTime: Infinity,
  });

  const { albums, singlesEps } = arrangeRecommendations(recs, { filter, hidden, showSinglesEps });
  const hiddenHere = recs.filter((r) => hidden.includes(r.albumId) && (filter === 'all' || r.source === filter)).length;

  const hide = (r: Recommendation) => {
    setHidden((h) => (h.includes(r.albumId) ? h : [...h, r.albumId]));
    toast({
      message: `Hid ${r.title}`,
      action: { label: 'Undo', onClick: () => setHidden((h) => h.filter((id) => id !== r.albumId)) },
    });
  };

  return (
    <div className="page home">
      <DiscoverWeekly covers={recs.filter((r) => r.kind === 'album').map((r) => r.cover)} />

      <section aria-labelledby="for-you" className="for-you">
        <div className="for-you-head">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <h2 id="for-you" className="disp section-title">
              New listening, for you
            </h2>
            <p className="muted" style={{ margin: 0, fontSize: 15 }}>
              Albums first.<span className="desktop-only"> Built from your taste doc and the artists you open.</span>
            </p>
          </div>
          <div role="group" aria-label="Filter recommendations" className="chips">
            {FILTERS.map((f) => (
              <button key={f.id} type="button" className="chip" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>
                {f.id !== 'all' && <span className={`dot${f.id === 'searches' ? ' square' : ''}`} aria-hidden="true" />}
                <span className="desktop-only">{f.label}</span>
                <span className="mobile-only">{f.short}</span>
              </button>
            ))}
          </div>
        </div>

        {isPending ? (
          <div className="rec-grid" aria-busy="true" aria-label="Loading recommendations">
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} className="rec">
                <div className="skeleton rec-cover" style={{ aspectRatio: '1' }} />
                <div className="rec-text">
                  <div className="skeleton" style={{ height: 14, width: '80%' }} />
                  <div className="skeleton" style={{ height: 12, width: '55%', marginTop: 6 }} />
                  <div className="skeleton" style={{ height: 24, width: '90%', marginTop: 10 }} />
                </div>
              </div>
            ))}
          </div>
        ) : albums.length > 0 ? (
          <div className="rec-grid">
            {albums.map((r) => (
              <RecCard key={r.albumId} r={r} onHide={hide} />
            ))}
          </div>
        ) : (
          <p className="notice">
            <span style={{ flex: 1 }}>Nothing left here. You hid {hiddenHere === 1 ? 'the last album' : 'these albums'}.</span>
            <button
              type="button"
              className="chip"
              onClick={() => setHidden((h) => h.filter((id) => !recs.some((r) => r.albumId === id && (filter === 'all' || r.source === filter))))}
            >
              Show hidden ({hiddenHere})
            </button>
          </p>
        )}

        {singlesEps.length > 0 && (
          <section aria-labelledby="singles-eps" className="singles-eps">
            <h3 id="singles-eps" className="disp" style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>
              Singles &amp; EPs
            </h3>
            <div className="rec-grid">
              {singlesEps.map((r) => (
                <RecCard key={r.albumId} r={r} onHide={hide} />
              ))}
            </div>
          </section>
        )}

        <div className="more-row">
          <span className="muted" style={{ flex: 1, minWidth: 220, fontSize: 14 }}>
            {showSinglesEps ? 'Singles and EPs are listed after the albums.' : 'That’s the albums. Want a few singles and EPs too?'}
          </span>
          <button type="button" className="chip" aria-pressed={showSinglesEps} onClick={() => setShowSinglesEps((s) => !s)}>
            {showSinglesEps ? 'Hide singles & EPs' : 'Show singles & EPs'}
          </button>
        </div>
      </section>

      <aside className="notice">
        <BlockIcon />
        <span style={{ flex: 1, minWidth: 220 }}>
          Never shown here: podcasts, audiobooks, AI-generated music, and new releases from artists you don’t follow.
        </span>
        <Link to="/taste#never" style={{ color: 'var(--accent-soft)', fontWeight: 600, minHeight: 44, display: 'inline-flex', alignItems: 'center' }}>
          Edit filters
        </Link>
      </aside>
    </div>
  );
}
